import { useState } from "react";
import axios from "axios";
import { z } from "zod";
import { getData, storeData } from "@/lib/utils";
import api from "@/store/api";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { ProfileSchema } from "@/schema";
import useLogin from "./use-login";

const useProfile = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const { refreshToken } = useLogin();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaveLoading, setisSaveLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const registerUser = async (values: z.infer<typeof ProfileSchema>) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.post("/api/profile", values);
      setIsLoading(false);
      
      return response.data;
    } catch (err) {
      setIsLoading(false);
      if (axios.isAxiosError(err)) {
        setError(
          err.response?.data?.message || t("alerts.error.registeringUser")
        );
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  };

  const updateUser = async (values: z.infer<typeof ProfileSchema>) => {
    setisSaveLoading(true);
    setError(null);
    try {
      let Token = getData("tokens")?.accessToken;
      const response = await api.post(`/web/auth/upsert`, values, {
        headers: {
          Authorization: `Bearer ${Token}`,
        },
      });
      setisSaveLoading(false);
      return response.data;
    } catch (err) {
      setisSaveLoading(false);
      if (axios.isAxiosError(err)) {
        setError(
          err.response?.data?.message || t("alerts.error.updatingProfile")
        );
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  };

  const getUserProfile = async () => {
    setIsLoading(true);
    setError(null);
    try {
      let Token = getData("tokens")?.accessToken;
      const response = await api.get(`/web/authInfo/profile/`, {
        headers: {
          Authorization: `Bearer ${Token}`,
        },
      });
      setIsLoading(false);
      return response.data;
    } catch (err) {
      setIsLoading(false);
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 404) {
          return null;
        }
        setError(
          err.response?.data?.message || t("alerts.error.fetchingProfile")
        );
        if (err.response?.data?.details === "Unauthorized") {
          const res = await refreshToken();
          if (res) {
            storeData("tokens", res.data.tokens);
            window.location.reload();
          }
        }
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  };

  return {
    registerUser,
    updateUser,
    getUserProfile,
    isLoading,
    isSaveLoading,
    error,
  };
};

export default useProfile;
